import React, {useEffect} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageHeader from '@/shared/layout/PageHeader';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppSelector, useAppDispatch} from '@/shared/hooks/redux';
import {useTestsState, useTestsByName} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {
    selectCurrentPatient,
    selectSubPatients,
    setCurrentPatient,
    restoreCurrentPatientFromStorage,
} from '@/slices/patientSlice';
import Icon from '@/shared/components/Icon';
import BioMatchCardSurface from '@/modules/biomatch/components/BioMatchCardSurface';
import {determineStatus} from '@/shared/utils/biomarkerUtils';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';

const Results: React.FC = () => {
    const {user} = useAuth();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const {rules} = useDisplayRules();

    // Get gender from user info for gender-specific biomarker configs (see LabResults.tsx)
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Get status filter from URL parameter
    const statusFilter = searchParams.get('status'); // 'normal', 'borderline', or 'critical'

    // Redux selectors for current patient and test data
    const currentPatient = useAppSelector(selectCurrentPatient);
    const subPatients = useAppSelector(selectSubPatients);
    const testsState = useTestsState();
    const currentPatientData = useTestsByName(currentPatient?.fullName || '');

    // Restore current patient from localStorage on page load (handles token refresh scenarios)
    useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && user?.token) {
            dispatch(restoreCurrentPatientFromStorage());
        }
    }, [dispatch, currentPatient?.id, user?.token]);

    // Set current patient from subPatients data (extracted from test results)
    useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && subPatients.length > 0) {
            // Try to find the previously selected patient from localStorage first
            let targetPatient: any = null;

            try {
                const storedPatient = localStorage.getItem('currentPatient');
                if (storedPatient) {
                    const parsed = JSON.parse(storedPatient);
                    // Try to find the stored patient in the current subPatients list
                    targetPatient = subPatients.find(
                        (p) =>
                            p.id === parsed.id || (p.firstName === parsed.firstName && p.lastName === parsed.lastName)
                    );
                    if (targetPatient) {
                        // Found the target patient
                    }
                }
            } catch {
                // Silently handle localStorage read errors
            }

            // If we couldn't find the stored patient, use the first valid one
            if (!targetPatient) {
                targetPatient = subPatients.find((p) => p.id && p.id > 0 && p.firstName && p.lastName);
            }

            if (targetPatient) {
                dispatch(setCurrentPatient(targetPatient));
            }
        }
    }, [dispatch, currentPatient, subPatients]);

    // Filter results for tests that should be shown in results page (same logic as old Results.tsx)
    const results = React.useMemo(() => {
        let filtered = currentPatientData.filter((r: any) => {
            const status = r.status?.toLowerCase();
            return status === 'collected' || status === 'received' || status === 'resulted' || status === 'processing';
        });

        // Apply biomarker status filter if provided
        if (statusFilter && ['normal', 'borderline', 'critical'].includes(statusFilter)) {
            filtered = filtered.filter((test: any) => {
                if (!test.results || !Array.isArray(test.results)) return false;

                // Check if test has any biomarkers matching the status filter
                return test.results.some((result: any) => {
                    const biomarkerStatus = determineStatus(result, gender, rules);

                    if (statusFilter === 'normal') {
                        return biomarkerStatus === 'Normal';
                    } else if (statusFilter === 'borderline') {
                        return biomarkerStatus === 'Low';
                    } else if (statusFilter === 'critical') {
                        return biomarkerStatus === 'High';
                    }
                    return false;
                });
            });
        }

        return filtered;
    }, [currentPatientData, statusFilter, gender, rules]);

    // Show loading state while data is being fetched
    if (testsState.loading) {
        return (
            <DashboardLayout>
                <PageHeader title="My Lab Results" subtitle="View all your lab tests and results" />
                <div className="flex items-center justify-center py-12">
                    <Icon name="spinner" spin className="h-8 w-8 text-mld-primary" />
                    <span className="ml-2 text-mld-text-primary">Loading your lab results...</span>
                </div>
            </DashboardLayout>
        );
    }

    const getStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case 'resulted':
                return <Icon name="circle-check" className="h-5 w-5 text-mld-success" />;
            case 'processing':
            case 'received':
            case 'collected':
                return <Icon name="clock" className="h-5 w-5 text-yellow-500" />;
            default:
                return <Icon name="vial" className="h-5 w-5 text-gray-500" />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status.toLowerCase()) {
            case 'resulted':
                return 'Complete';
            case 'processing':
                return 'Processing';
            case 'received':
                return 'Received';
            case 'collected':
                return 'Collected';
            default:
                return 'Pending';
        }
    };

    return (
        <DashboardLayout>
            <PageHeader title="My Lab Results" subtitle="View all your lab tests and results" />

            <div className="space-y-6">
                {results.length > 0 ? (
                    <div className="grid gap-4">
                        {results.map((result: any) => {
                            const status = result.status?.toLowerCase();
                            const createdDate = result.createdDate
                                ? new Date(result.createdDate).toLocaleDateString()
                                : new Date(result.statusDate).toLocaleDateString();

                            // Determine test name from OID or use a fallback
                            const getTestName = () => {
                                const oidPrefix = result.oid?.substring(result.oid.length - 10, result.oid.length - 8);
                                switch (oidPrefix) {
                                    case 'BW':
                                        return 'Basic Wellness Panel';
                                    case 'AL':
                                        return 'Allergy Test';
                                    case 'ST':
                                        return 'STD Panel';
                                    case 'CV':
                                        return 'COVID-19 Test';
                                    default:
                                        return `Test ${result.id}`;
                                }
                            };

                            const testName = getTestName();

                            if (
                                status === 'collected' ||
                                status === 'received' ||
                                status === 'processing' ||
                                result.unreleased
                            ) {
                                return (
                                    <div
                                        key={result.id}
                                        className="bg-mld-menu-background rounded-2xl shadow-sm border border-mld-panel-border p-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-mld-text-primary mb-2">
                                                    {testName}
                                                </h3>
                                                <p className="text-sm text-mld-muted-text">Order Date: {createdDate}</p>
                                                {result.appointmentDate && (
                                                    <p className="text-sm text-mld-muted-text">
                                                        Appointment:{' '}
                                                        {new Date(result.appointmentDate).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {getStatusIcon(result.status)}
                                                <span className="text-sm font-medium text-mld-text-primary">
                                                    {getStatusText(result.status)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            } else if (status === 'resulted') {
                                return (
                                    <Link
                                        key={result.id}
                                        to={
                                            statusFilter
                                                ? `/result/${result.id}?status=${statusFilter}`
                                                : `/result/${result.id}`
                                        }
                                        className="block">
                                        <div className="bg-mld-menu-background rounded-2xl shadow-sm border border-mld-panel-border p-6 cursor-pointer">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-semibold text-mld-text-primary mb-2">
                                                        {testName}
                                                    </h3>
                                                    <p className="text-sm text-mld-muted-text">
                                                        Completed: {new Date(result.statusDate).toLocaleDateString()}
                                                    </p>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {getStatusIcon(result.status)}
                                                    <span className="text-sm font-medium text-mld-success hover:text-mld-success transition-colors">
                                                        View Results →
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Recommendations Section */}
                                            {result.recommendations && result.recommendations.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-mld-panel-border">
                                                    <BioMatchCardSurface
                                                        recommendations={result.recommendations}
                                                        compact
                                                        maxVisible={2}
                                                        onViewAll={() =>
                                                            (window.location.href = `/result/${result.id}`)
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                );
                            }
                            return null;
                        })}
                    </div>
                ) : (
                    <div className="bg-mld-menu-background rounded-2xl shadow-sm border border-mld-panel-border p-12 text-center">
                        <Icon name="vial" className="h-16 w-16 text-mld-muted-text mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-mld-text-primary mb-2">No Lab Results Yet</h3>
                        <p className="text-mld-muted-text mb-6">
                            Your lab results will appear here once they&apos;re available.
                            {currentPatient && (
                                <span className="block mt-2 text-sm">
                                    Viewing results for: {currentPatient.fullName}
                                </span>
                            )}
                        </p>
                        <Link
                            to="/test-kits"
                            className="inline-block px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors">
                            Order a Test Kit
                        </Link>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Results;
